import {trackEvent} from '@phntms/next-gtm';
import {IBannersResponse, ICartItem, ICustomerAddress, ICustomerResponse, IOrder, IProduct} from 'types';
import logger from 'utils/logger';

const GTMService = {
  login: (user: ICustomerResponse, address?: ICustomerAddress) => {
    const _user = {
      user_id: user.id,
      user_email: user.email,
      user_phone: user.phonenumber,
      user_first_name: user.name,
      user_address: {
        street: address?.address?.street ?? '',
        city: address?.address?.city ?? '',
        country: address?.address?.country ?? 'MD',
      },
      user_type: 'registered',
      method: 'phone',
      timestamp: new Date().toISOString(),
      google_analytics_customers: {
        email: user.email,
        phone: user.phonenumber,
        first_name: user.name,
        street: address?.address?.street ?? '',
        city: address?.address?.city ?? '',
        country: address?.address?.country ?? 'MD',
      },
    };

    trackEvent({
      event: 'login',
      data: _user,
    });
  },

  signUp: (user: ICustomerResponse) => {
    const _user = {
      user_id: user.id,
      user_email: user.email,
      user_phone: user.phonenumber,
      user_first_name: user.name,
      signup_method: 'phone',
      signup_source: window?.document?.URL,
      timestamp: new Date().toISOString(),
      google_analytics_customers: {
        email: user.email,
        phone: user.phonenumber,
        first_name: user.name,
      },
    };

    trackEvent({
      event: 'sign_up',
      data: _user,
    });
  },

  getAddress: (user: ICustomerResponse, address: ICustomerAddress) => {
    const addressObj = {
      street: address?.address?.street ?? '',
      city: address?.address?.city ?? '',
      country: address?.address?.country ?? 'MD',
    };
    const _user = {
      user_id: user.id,
      user_email: user.email,
      user_phone: user.phonenumber,
      user_first_name: user.name,
      shipping_address: {
        ...addressObj,
      },
      billing_address: {
        ...addressObj,
      },
      timestamp: new Date().toISOString(),
      google_analytics_customers: {
        email: user.email,
        phone: user.phonenumber,
        first_name: user.name,
        last_name: '',
        ...addressObj,
      },
    };
    logger.logDebug(`get_address:${_user}`, 'gtmService');
    trackEvent({
      event: 'get_address',
      data: _user,
    });
  },

  search: (searchTerm: string, resultsCount: number, category: string) => {
    logger.logDebug(`search:${searchTerm}`, 'gtmService');
    trackEvent({
      event: 'search',
      data: {
        search_term: searchTerm,
        results_count: resultsCount,
        search_category: category,
        timestamp: new Date().toISOString(),
      },
    });
  },

  viewItemList: (
    pathData: {category: string; path: string[]},
    restaurant: {id: number; name: string; slug: string},
  ) => {
    logger.logDebug(`viewitemsList:${restaurant}`, 'gtmService');
    trackEvent({data: {ecommerce: null}}); // Очистка предыдущего объекта ecommerce

    const categories = pathData.path;
    const categoryObj: {[key: string]: string} = {};
    categories
      .filter((prev) => prev != '')
      .forEach((category, index) => {
        categoryObj[`item_category${index + 1}`] = category;
      });
    logger.logDebug(`viewitemsList:TRACKING:${JSON.stringify(categoryObj)}`, 'gtmService');

    trackEvent({
      event: 'view_item_list',
      data: {
        ecommerce: {
          items: [
            {
              item_id: restaurant.id.toString(),
              item_name: restaurant.name,
              item_brand: restaurant.slug,

              item_category: pathData.category, //restaurant|market
              ...categoryObj,
            },
          ],
        },
      },
    });
  },

  selectPromotion: (banner: IBannersResponse) => {
    logger.logDebug(`select_promotion:${banner}`, 'gtmService');
    trackEvent({data: {ecommerce: null}}); // Очистка предыдущего объекта ecommerce

    trackEvent({
      event: 'select_promotion',
      data: {
        ecommerce: {
          promotion_id: banner.id.toString(),
          promotion_name: banner.source_slug,
          creative_name: banner.name,
          creative_slot: banner.source_slug,
          location_id: 'home_landing',
        },
      },
    });
  },

  viewItem: (item: IProduct, providerSlug: string) => {
    logger.logDebug(`view_item:${providerSlug}`, 'gtmService');
    trackEvent({data: {ecommerce: null}}); // Очистка предыдущего объекта ecommerce

    trackEvent({
      event: 'view_item',
      data: {
        ecommerce: {
          items: [
            {
              item_id: item.id.toString(),
              item_name: item.name,
              item_brand: providerSlug,
              item_category: item?.menu_category?.name ?? '',
              price: item.price,
              currency: 'MDL',
              quantity: 1,
              item_list_name: providerSlug, //restaurant name
              item_list_id: item?.menu_category?.id?.toString(),
              location_id: providerSlug,
              promotion_id: item?.discount ? item?.discount_price : '',
              promotion_name: item?.discount ? `${item.discount}` : '',
            },
          ],
        },
      },
    });
  },
  /**
   *
   *  CART+CHECKOUT
   *
   */
  addToCart: (items: ICartItem[], providerSlug: string) => {
    logger.logDebug(`add_to_cart`, 'gtmService');
    trackEvent({data: {ecommerce: null}}); // Очистка предыдущего объекта ecommerce

    trackEvent({
      event: 'add_to_cart',
      data: {
        ecommerce: {
          items: transformCartItemsToGTM(items, providerSlug),
        },
      },
    });
  },

  removeFromCart: (items: ICartItem[], providerSlug: string) => {
    logger.logDebug(`remove_from_cart`, 'gtmService');
    trackEvent({data: {ecommerce: null}}); // Очистка предыдущего объекта ecommerce

    trackEvent({
      event: 'remove_from_cart',
      data: {
        ecommerce: {
          currency: 'MDL',
          value: items.reduce((total, item) => total + item.price * item.quantity, 0),
          items: transformCartItemsToGTM(items, providerSlug),
        },
      },
    });
  },

  viewCart: (cartItems: ICartItem[], totalValue: number, providerSlug: string) => {
    logger.logDebug(`view_cart`, 'gtmService');
    trackEvent({data: {ecommerce: null}}); // Очистка предыдущего объекта ecommerce

    trackEvent({
      event: 'view_cart',
      data: {
        ecommerce: {
          currency: 'MDL',
          value: totalValue,
          items: transformCartItemsToGTM(cartItems, providerSlug),
        },
      },
    });
  },

  beginCheckout: (items: ICartItem[], providerSlug: string, totalValue: number, coupon?: string) => {
    logger.logDebug(`begin_checkout:${providerSlug}`, 'gtmService');
    trackEvent({data: {ecommerce: null}}); // Очистка предыдущего объекта ecommerce

    trackEvent({
      event: 'begin_checkout',
      data: {
        ecommerce: {
          currency: 'MDL',
          value: totalValue,
          coupon: coupon ?? '',
          items: transformCartItemsToGTM(items, providerSlug),
        },
      },
    });
  },

  purchase: (order: IOrder, user: ICustomerResponse) => {
    logger.logDebug(`purchase:${order.uuid}`, 'gtmService');
    trackEvent({data: {ecommerce: null}}); // Очистка предыдущего объекта ecommerce

    trackEvent({
      event: 'purchase',
      data: {
        ecommerce: {
          transaction_id: order.uuid,
          payment_type: order.payment_type_str,
          value: order.money.order_total,
          shipping: order.money.delivery_cost,
          currency: 'MDL',
          coupon: order.money.promo_code ?? '',
          items: order.items.map((item) => ({
            item_id: item.product.id.toString(),
            item_name: item.product.name,
            item_brand: order.provider.slug,
            price: item.price,
            currency: 'MDL',
            quantity: item.quantity,
            location_id: order.provider.slug,
          })),
          // Данные для расширенных конверсий Google Ads
          user_data: {
            email: user.email,
            phone: user.phonenumber,
            first_name: user.name,
            address: {
              street: order?.address?.street,
              city: order?.address?.city,
              country: 'MD',
            },
          },
        },
      },
    });
  },

  refund: (order: IOrder) => {
    logger.logDebug(`refund:${order.uuid}`, 'gtmService');
    trackEvent({data: {ecommerce: null}}); // Очистка предыдущего объекта ecommerce

    trackEvent({
      event: 'refund',
      data: {
        ecommerce: {
          transaction_id: order.uuid,
          currency: 'MDL',
          payment_type: order.payment_type_str,
          value: order.money.order_total,
          shipping: order.money.delivery_cost,
          coupon: order.money.promo_code ?? '',
          items: order.items.map((item) => ({
            item_id: item.product.id.toString(),
            item_name: item.product.name,
            item_brand: order.provider.slug,
            price: item.price,
            currency: 'MDL',
            quantity: item.quantity,
            location_id: order.provider.slug,
          })),
        },
      },
    });
  },

  addToWishlist: (id: number, slug: string) => {
    logger.logDebug(`add_to_wishlist`, 'gtmService');
    trackEvent({data: {ecommerce: null}}); // Очистка предыдущего объекта ecommerce

    const wishlistItems = {
      item_id: id.toString(),
      item_name: slug,
      item_brand: slug,
    };

    trackEvent({
      event: 'add_to_wishlist',
      data: {
        ecommerce: {
          currency: 'MDL',
          value: 1,
          items: wishlistItems,
        },
      },
    });
  },

  addPaymentInfo: (
    items: ICartItem[],
    paymentType: string,
    totalValue: number,
    providerSlug: string,
    coupon?: string,
  ) => {
    logger.logDebug(`add_payment_info`, 'gtmService');
    trackEvent({data: {ecommerce: null}}); // Очистка предыдущего объекта ecommerce

    trackEvent({
      event: 'add_payment_info',
      data: {
        ecommerce: {
          currency: 'MDL',
          value: totalValue,
          coupon: coupon ?? '',
          payment_type: paymentType,
          items: transformCartItemsToGTM(items, providerSlug),
        },
      },
    });
  },
};

/**
 * transformCartItemsToGTM
 * @returns
 */
const transformCartItemsToGTM = (items: ICartItem[], providerSlug: string) => {
  return items.map((item) => {
    const product = item.product;
    return {
      item_id: product.id.toString(),
      item_name: product.name,
      item_brand: providerSlug,
      item_category: product?.menu_category?.name ?? '',
      price: item.price,
      currency: 'MDL',
      quantity: item.quantity,
      location_id: providerSlug,
    };
  });
};
export default GTMService;
